import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { LocationSearchResultsCardComponent } from '../location-search-results-card/location-search-results-card.component'; 
import { SearchStateService } from '../../shared/services/search-state.service'; 
import { LocationCoordinates } from '../../../shared/model/LocationCoordinates';
import { MCAppGatewayService } from '../../../shared/services/mcappgateway/mcappgateway.service'; 
import { PostGetLocationSearchRequest } from '../../../shared/model/providerlocationsearch/PostGetProviderSearchRequest';
import { TranslateService } from '@ngx-translate/core';
import { MonitoringService } from '../../../shared/services/monitoring/monitoring-service';
import { LocationSearchFilters, SearchFilter } from '../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';

@Component({
  selector: 'app-location-search-results',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatMenuModule, MatRadioModule, LocationSearchResultsCardComponent, MatProgressSpinner, MatAutocompleteModule],
  templateUrl: './location-search-results.component.html',
  styleUrl: './location-search-results.component.css'
})
export class LocationSearchResultsComponent {
  searchState = inject(SearchStateService);
  mcAppGateway = inject(MCAppGatewayService);

  @Input() windowResolution: number = 0;
  @Input() locale: string = '';
  @Input() currentLocation: LocationCoordinates | null = null;
  @Input() cityTypeAheadOptions: string[] = [];

  showSpinner$ = this.searchState.showSpinner$;
  locationSearchQuery$ = this.searchState.locationSearchQuery$;
  locationSearchResults$ = this.searchState.locationSearchResults$;
  locationSearchFilters$ = this.searchState.locationSearchFilters$;
  locationSearchFilterToggle$ = this.searchState.locationSearchFilterToggle$;
  locationSortMethod$ = this.searchState.locationSortMethod$;

  showSpinner: boolean = false;
  allResults: any[] = [];
  currentFilters: LocationSearchFilters | null = null;
  queryLocationName: string = '';
  queryCityOrZip: string = '';
  appliedFilterCount: number = 0;
  distanceSliderValue: number = 2;
  locationSortMethod: 'distance' | 'availability' | 'name' = 'availability';

  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 1;
  visiblePages: number[] = [];

  // Typeahead properties
  cityOrZipControl = new FormControl('');
  filteredCityOptions$: Observable<string[]> = new Observable<string[]>();

  newSearchBtnText = "New Search";
  breadcrumbText = "Location Search";
  breadcrumbText2 = "Search Results";
  resultsText = "Results";
  sortByTitle = "Sort by";
  distanceLabel = "Distance";
  availabilityLabel = "Soonest Availability"
  nameLabel = "Name (A-Z)";
  mileText = "mile";
  clearBtnText = "Clear";
  applyBtnText = "Apply";
  locationLabel = "Location";

  constructor(private translate: TranslateService, private monitoringService: MonitoringService) {
      this.locale =  sessionStorage.getItem('locale') || 'en-US';
      this.translate.use(this.locale); // Default to 'en-US' if no locale is provided
    }

  ngOnInit(): void {
    this.showSpinner$.subscribe(show => {
      this.showSpinner = show;
    });

    // Load translations
    this.translate.get('NEW SEARCH').subscribe((translated: string) => {
      this.newSearchBtnText = translated;
    });

    this.translate.get('LOCATION_SEARCH').subscribe((translated: string) => {
      this.breadcrumbText = translated;
    });

    this.translate.get('SEARCH_RESULTS').subscribe((translated: string) => {
      this.breadcrumbText2 = translated;
    });

    this.translate.get('RESULTS').subscribe((translated: string) => {
      this.resultsText = translated;
    });

    this.translate.get('SORT_BY.TITLE').subscribe((translated: string) => {
      this.sortByTitle = translated;
    });

    this.translate.get('SORT_BY.DISTANCE').subscribe((translated: string) => {
      this.distanceLabel = translated;
    });

    this.translate.get('SORT_BY.AVAILABILITY').subscribe((translated: string) => {
      this.availabilityLabel = translated;
    });

    this.translate.get('SORT_BY.NAME').subscribe((translated: string) => {
      this.nameLabel = translated;
    });       

    this.translate.get('MILE').subscribe((translated: string) => {
        this.mileText = translated;
    });

    this.translate.get('APPLY').subscribe((translated: string) => {
      this.applyBtnText = translated;
    });

    this.translate.get('CLEAR').subscribe((translated: string) => {
      this.clearBtnText = translated;
    });

    this.translate.get('LOCATION').subscribe((translated: string) => {
      this.locationLabel = translated;
    });

    this.locationSearchQuery$.subscribe(query => {
      if (query) {
        this.queryLocationName = query.facilityOrLocationName;
        this.queryCityOrZip = query.cityOrZip;
        this.cityOrZipControl.setValue(query.cityOrZip, { emitEvent: false });
      }
    });

    this.windowResolution = window.innerWidth;
    addEventListener('resize', () => {
      this.windowResolution = window.innerWidth;
    });

    this.locationSearchFilters$.subscribe(filters => {
      this.currentFilters = filters;
      let count = 0;
      if (filters) {
        if (filters.typeList) {
          count += filters.typeList.filter((option: SearchFilter) => option.isSelected == true).length;
        }
        if (filters.serviceList) {
          count += filters.serviceList.filter((option: SearchFilter) => option.isSelected == true).length;
        }
        this.appliedFilterCount = count;
        if (this.currentFilters && this.currentFilters.distanceInMiles && this.currentFilters.distanceInMiles > 0) {
          this.setDistanceFilterValue(this.currentFilters.distanceInMiles);
        }
      }
    });

    this.locationSearchResults$.subscribe(results => {
      if (results) {
        this.allResults = results;
        this.totalPages = Math.ceil(results.length / this.itemsPerPage);
        this.currentPage = 1;
        this.updateVisiblePages();
      }
    });

    this.locationSortMethod$.subscribe(sortMethod => {
      this.locationSortMethod = sortMethod;
    });

    // Setup autocomplete filtering for city/zip
    this.filteredCityOptions$ = this.cityOrZipControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCityOptions(value || ''))
    );

    // Sync the FormControl with queryCityOrZip
    this.cityOrZipControl.valueChanges.subscribe(value => {
      this.queryCityOrZip = value || '';
    });
  }

  private _filterCityOptions(value: string): string[] {
    // Only show suggestions if 3 or more characters have been typed
    if (!value || value.length < 3) {
      return [];
    }

    if (!this.cityTypeAheadOptions || !Array.isArray(this.cityTypeAheadOptions)) {
      console.log('No city type ahead options available');
      return [];
    }

    // Remove special characters and convert to lowercase for comparison
    const normalizeString = (str: string) => str.toLowerCase().replace(/[^a-z0-9\s]/gi, '');
    const filterValue = normalizeString(value);
    
    const filtered = this.cityTypeAheadOptions.filter(option => 
      normalizeString(option).includes(filterValue)
    );
    console.log('Filtered city options:', filtered);
    return filtered;
  }

  getPaginatedResults(): any[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.allResults.slice(startIndex, endIndex);
  }

  updateVisiblePages(): void {
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    this.visiblePages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updateVisiblePages();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  setDistanceFilterValue(distanceInMiles: number): void {
    switch (distanceInMiles) {
      case 5: this.distanceSliderValue = 0; break;
      case 10: this.distanceSliderValue = 1; break;
      case 15: this.distanceSliderValue = 2; break;
      case 25: this.distanceSliderValue = 3; break;
      case 50: this.distanceSliderValue = 4; break;
      default: this.distanceSliderValue = 2;
    }
  }

  getDistanceInMilesFromSliderValue(sliderValue: number): number {
    switch (sliderValue) {
      case 0: return 5;
      case 1: return 10;
      case 2: return 15;
      case 3: return 25;
      case 4: return 50;
      default: return 15;
    }
  }

  setDistanceSliderValueFromLabel(value: number): void {
    this.distanceSliderValue = value;
  }

  onClearLocationFilter() {
    this.queryCityOrZip = this.currentFilters?.location || '';
    this.cityOrZipControl.setValue(this.queryCityOrZip, { emitEvent: false });

    if (this.currentFilters &&  this.currentFilters.distanceInMiles && this.currentFilters.distanceInMiles > 0) {
      this.setDistanceFilterValue(this.currentFilters.distanceInMiles);
    }
  }

  onApplySortMethod(sortBy: 'distance' | 'availability' | 'name') {
    this.searchState.setLocationSortMethod(sortBy);
    this.searchState.setLocationSearchResults(this.searchState.sortLocationSearchResults(this.allResults, sortBy));
  }

  onLocationMenuClosed() {
    this.onClearLocationFilter();
  }

  onApplyLocationFilter() {
    if (this.currentFilters) {
      var request: PostGetLocationSearchRequest = new PostGetLocationSearchRequest();
      request.locationName = this.currentFilters.facilityOrLocationName;
      console.log(this.currentFilters);
      if ((request.locationName == null || request.locationName == '') && this.queryLocationName != '') {
        request.locationName = this.queryLocationName;
      }
      request.cityOrZip = this.queryCityOrZip;
      request.distanceInMiles = this.getDistanceInMilesFromSliderValue(this.distanceSliderValue) ?? 15;
      request.services = this.currentFilters?.serviceList?.filter(s => s.isSelected).map(s => s.value).join(', ');
      request.types = this.currentFilters?.typeList?.filter(t => t.isSelected).map(t => t.value).join(', ');
      if ((request.cityOrZip == null || request.cityOrZip == "") && this.currentLocation != null && this.currentLocation.latitude && this.currentLocation.longitude) {
        request.latitude = this.currentLocation.latitude;
        request.longitude = this.currentLocation.longitude;
      }
      this.searchState.setShowSpinner(true);
      this.mcAppGateway.PostGetLocationSearch(request).subscribe({
        next: (response) => {
          if (this.currentFilters != null) {
            this.currentFilters.location = request.cityOrZip ?? '';
            this.currentFilters.distanceInMiles = request.distanceInMiles ?? 15;
            this.searchState.setLocationSearchFilters(this.currentFilters);
            this.searchState.setLocationSearchQuery({
              facilityOrLocationName: this.currentFilters.facilityOrLocationName != '' ? this.currentFilters.facilityOrLocationName : this.queryLocationName,
              cityOrZip: this.queryCityOrZip,
            });
          }
          var sortedResponse = this.searchState.sortLocationSearchResults(response || [], this.locationSortMethod);
          this.searchState.setLocationSearchResults(sortedResponse);
          this.searchState.setShowSpinner(false);
        },
        error: (error) => {
          alert('An error occurred while fetching location search results. Please try again later.');
          this.searchState.setShowSpinner(false);
        }
      });
    }
  }

  clearSearch(): void {
    
    this.searchState.setLocationSearchQuery({ facilityOrLocationName: "", cityOrZip: this.queryCityOrZip })
    this.searchState.clearLocationSearchResults();
    this.clearFilters();
    this.searchState.setLocationSearchFilterToggle(false);
    this.searchState.clearCurrentLocationDetails();
  }

  clearFilters(): void {
    if (this.currentFilters) {
      // Clear all selections
      this.currentFilters.typeList?.forEach(option => option.isSelected = false);
      this.currentFilters.serviceList?.forEach(option => option.isSelected = false);

      this.searchState.setLocationSearchFilters(this.currentFilters);
    }
  }
}